<template>
  <PageLayout
    :is-loading="isLoading"
    page-title="Pointers of the Month"
    :show-back-button="true"
    :show-banner="false"
    :show-sponsors="false"
    :hide-sidebar="true"
    @back="goBack"
  >
    <!-- Current Pointer Section -->
    <div class="pointers-section" v-if="currentPointer">
      <div class="section-container">
        <div class="section-header">
          <h2 class="section-title">Pointer of the Month</h2>
          <p class="section-subtitle">
            {{ formatMonthYear(currentPointer.month_year) }}<br>
            Selamat kepada pemenang bulan ini!
          </p>
        </div>
        
        <div class="pointers-bg">
          <div class="pointers-showcase">
            <div class="pointer-card featured">
              <div class="pointer-image-header">
                <img :src="getDisplayImage(currentPointer)" :alt="currentPointer.user_name" class="pointer-photo" />
                <div class="winner-badge">
                  <i class="mdi mdi-crown"></i>
                  Winner
                </div>
              </div>
              
              <div class="pointer-info">
                <h3 class="pointer-name">{{ currentPointer.user_name }}</h3>
                <div class="pointer-stats">
                  <div class="stat-item">
                    <span class="stat-label">Total Points</span>
                    <span class="stat-value">{{ formatPoints(currentPointer.user_points || 0) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No Active Pointer -->
    <div class="no-pointer-section" v-else-if="!isLoading">
      <div class="section-container">
        <div class="no-pointer-content">
          <div class="no-pointer-icon">
            <i class="mdi mdi-trophy-outline"></i>
          </div>
          <h3>Belum Ada Pointer Bulan Ini</h3>
          <p>Pointer of the Month untuk bulan ini belum ditentukan. Tetap kumpulkan BERT's Point untuk kesempatan menjadi pemenang bulan depan!</p>
        </div>
      </div>
    </div>
  </PageLayout>
</template>

<script>
import AppPageLayout from '@/components/ui/PageLayout.vue';

export default {
  name: "PointersOfTheMonth",
  components: { 
    PageLayout: AppPageLayout
  },
  data() {
    return {
      isLoading: false,
      currentPointer: null,
      topUsers: [],
      errors: []
    };
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    
    loading() {
      this.isLoading = true;
      setTimeout(() => {
        this.isLoading = false;
      }, 1000);
    },

    fetchCurrentPointer() {
      const baseURI = this.$settings.endPoint + "pointers-of-month/public";
      
      return this.$http
        .get(baseURI, { 
          timeout: 10000,
          headers: {
            'AppToken': this.$settings.jwtSecret,
          }
        })
        .then((response) => {
          if (response.data.status === 200) {
            this.currentPointer = response.data.datas.current_pointer;
            this.topUsers = response.data.datas.recent_pointers || [];
          }
        })
        .catch((error) => {
          console.error("Error fetching pointers data:", error);
        });
    },

    getDisplayImage(pointer) {
      if (pointer.custom_image) {
        return pointer.custom_image;
      }
      return this.getUserImageFromSnapshot(pointer);
    },

    getUserImage(user) {
      if (user?.image) {
        return user.image;
      }
      return require('@/assets/maternity.png');
    },

    formatPoints(points) {
      if (!points) return '0';
      return new Intl.NumberFormat('id-ID').format(points);
    },

    getUserImageFromSnapshot(pointer) {
      if (pointer?.user_image) {
        return pointer.user_image;
      }
      return require('@/assets/maternity.png');
    },

    formatMonthYear(monthYear) {
      if (!monthYear) return '';
      const date = new Date(monthYear + '-01');
      return date.toLocaleDateString('id-ID', { 
        year: 'numeric', 
        month: 'long' 
      });
    },

    getRankClass(index) {
      if (index === 0) return 'rank-gold';
      if (index === 1) return 'rank-silver';
      if (index === 2) return 'rank-bronze';
      return 'rank-default';
    },

    getRankIcon(index) {
      if (index === 0) return 'mdi mdi-trophy';
      if (index === 1) return 'mdi mdi-medal';
      if (index === 2) return 'mdi mdi-medal-outline';
      return '';
    },

    success(kata) {
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", this.$swal.stopTimer);
          toast.addEventListener("mouseleave", this.$swal.resumeTimer);
        },
      });
      Toast.fire({
        icon: "success",
        title: kata,
      });
    },

    error(kata) {
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", this.$swal.stopTimer);
          toast.addEventListener("mouseleave", this.$swal.resumeTimer);
        },
      });
      Toast.fire({
        icon: "error",
        title: kata,
      });
    }
  },
  mounted() {
    this.isLoading = true;
    
    // Fetch data from single endpoint
    this.fetchCurrentPointer().finally(() => {
      this.isLoading = false;
    });
  }
};
</script>

<style scoped>
/* ========================================= */
/* POINTERS SECTION STYLES                   */
/* ========================================= */
.pointers-section {
  padding: var(--space-20) 0;
  background: transparent;
}

.section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--space-12);
  border-radius: var(--space-12);
  background-color: white;
}

.section-header {
  text-align: center;
  margin-bottom: var(--space-12);
}

.section-title {
  font-size: var(--text-5xl);
  font-weight: var(--font-bold);
  color: var(--text-dark);
  margin: 0 0 var(--space-4) 0;
}

.section-subtitle {
  font-size: var(--text-lg);
  color: var(--text-dark-grey);
  margin: 0;
  line-height: 1.6;
}

.pointers-showcase {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
  gap: var(--space-12);
  transform: translateY(-60px);
}

.pointer-card {
  background: white;
  border-radius: 24px;
  padding: 0; 
  text-align: center;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08), 0 4px 8px rgba(0,0,0,0.02);
  max-width: 400px;
  width: 100%;
  transition: all 0.4s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.pointer-card.featured {
  border: 3px solid #FFD700;
  box-shadow: 0 20px 48px rgba(255, 215, 0, 0.2), 0 12px 32px rgba(0, 0, 0, 0.08);
}

.pointer-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.12);
}

.pointer-image-header {
  width: 100%;
  height: 280px;
  background-color: #f0f0f0;
  position: relative;
}

.pointer-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  border-radius: 0;
  border: none;
  display: block;
}

.winner-badge {
  position: absolute;
  top: 16px;
  right: 16px;
  background: linear-gradient(135deg, #FFD700, #FFA500);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
}

.winner-badge i {
  font-size: 16px;
}

.pointer-info {
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  flex-grow: 1;
}

.pointer-name {
  font-size: var(--text-3xl);
  font-weight: 800;
  color: var(--text-dark);
  margin: 0;
}

.pointer-email {
  font-size: var(--text-base);
  color: var(--text-dark-grey);
  margin: 0;
}

.pointer-stats {
  display: flex;
  gap: 24px;
  margin: 16px 0;
  justify-content: center;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.stat-label {
  font-size: 12px;
  color: var(--text-dark-grey);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-dark);
}

.stat-value.active {
  color: #10B981;
}

.pointer-description {
  margin-top: 16px;
  padding: 16px;
  background: #F9FAFB;
  border-radius: 12px;
  border-left: 4px solid #FFD700;
}

.pointer-description p {
  margin: 0;
  font-style: italic;
  color: var(--text-dark-grey);
  line-height: 1.5;
}

.pointers-bg {
  position: relative;
  margin-top: var(--space-20);
  padding-bottom: var(--space-20);
}

.pointers-bg::before {
  content: "";
  position: absolute;
  top: 80px;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, var(--yellow-main), #FFE066);
  border-radius: var(--radius-3xl);
  z-index: 0;
}

/* ========================================= */
/* NO POINTER SECTION                        */
/* ========================================= */
.no-pointer-section {
  padding: var(--space-20) 0;
}

.no-pointer-content {
  text-align: center;
  padding: var(--space-16);
}

.no-pointer-icon {
  font-size: 80px;
  color: var(--text-dark-grey);
  margin-bottom: var(--space-8);
}

.no-pointer-content h3 {
  font-size: var(--text-2xl);
  font-weight: 700;
  color: var(--text-dark);
  margin: 0 0 var(--space-4) 0;
}

.no-pointer-content p {
  font-size: var(--text-base);
  color: var(--text-dark-grey);
  line-height: 1.6;
  max-width: 500px;
  margin: 0 auto;
}

/* ========================================= */
/* TOP PERFORMERS SECTION                    */
/* ========================================= */
.top-performers-section {
  padding: var(--space-16) 0;
  background: #F9FAFB;
}

.top-performers-section .section-title {
  font-size: var(--text-3xl);
}

.performers-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 800px;
  margin: 0 auto;
}

.performer-item {
  display: flex;
  align-items: center;
  padding: 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.performer-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.rank-badge {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  margin-right: 16px;
  flex-shrink: 0;
}

.rank-gold {
  background: linear-gradient(135deg, #FFD700, #FFA500);
  color: white;
}

.rank-silver {
  background: linear-gradient(135deg, #C0C0C0, #A8A8A8);
  color: white;
}

.rank-bronze {
  background: linear-gradient(135deg, #CD7F32, #B8860B);
  color: white;
}

.rank-default {
  background: #E5E7EB;
  color: #374151;
}

.performer-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 16px;
  flex-shrink: 0;
}

.avatar-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.performer-details {
  flex: 1;
}

.performer-name {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0 0 4px 0;
}

.performer-email {
  font-size: 0.9rem;
  color: #6B7280;
  margin: 0;
}

.performer-score {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.points-value {
  font-size: 1.3rem;
  font-weight: 700;
  color: #4A90E2;
}

.points-label {
  font-size: 0.8rem;
  color: #6B7280;
}

/* ========================================= */
/* MOTIVATION SECTION                        */
/* ========================================= */
.motivation-section {
  padding: var(--space-16) 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.motivation-content {
  text-align: center;
  padding: var(--space-12);
  color: white;
}

.motivation-icon {
  font-size: 60px;
  margin-bottom: var(--space-6);
  opacity: 0.9;
}

.motivation-content h3 {
  font-size: var(--text-3xl);
  font-weight: 700;
  margin: 0 0 var(--space-4) 0;
}

.motivation-content p {
  font-size: var(--text-lg);
  line-height: 1.6;
  margin: 0 0 var(--space-8) 0;
  opacity: 0.9;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: white;
  color: #667eea;
  text-decoration: none;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  color: #5a67d8;
  text-decoration: none;
}

/* ========================================= */
/* MOBILE RESPONSIVE STYLES                  */
/* ========================================= */
@media (max-width: 768px) {
  .section-container {
    padding: var(--space-4);
  }

  .section-header {
    margin: 0;
  }
  
  .section-title {
    font-size: var(--text-3xl);
    text-align: center;
  }
  
  .section-subtitle {
    font-size: var(--text-base);
    text-align: center;
  }

  .pointers-bg {
    margin: 0;
  }
  
  .pointers-section {
    padding: var(--space-8) 0;
  }
  
  .pointers-bg::before {
    top: 60px;
    border-radius: var(--radius-2xl);
  }

  .pointers-showcase {
    transform: translateY(10px);
  }

  .pointer-card {
    max-width: 280px;
    width: 50%;
    flex: 1;
  }

  .pointer-image-header {
    height: 200px;
  }

  .pointer-info {
    padding: 24px 16px;
  }

  .pointer-name {
    font-size: var(--text-2xl);
  }

  .pointer-stats {
    gap: 16px;
  }

  .stat-value {
    font-size: 16px;
  }

  .top-performers-section {
    padding: var(--space-8) 0;
  }

  .performer-item {
    padding: 16px;
  }

  .rank-badge {
    width: 40px;
    height: 40px;
    font-size: 1rem;
    margin-right: 12px;
  }

  .performer-avatar {
    width: 50px;
    height: 50px;
    margin-right: 12px;
  }

  .performer-name {
    font-size: 1rem;
  }

  .performer-email {
    font-size: 0.8rem;
  }

  .points-value {
    font-size: 1.1rem;
  }

  .points-label {
    font-size: 0.7rem;
  }

  .motivation-section {
    padding: var(--space-8) 0;
  }

  .motivation-content {
    padding: var(--space-8);
  }

  .motivation-icon {
    font-size: 48px;
  }

  .motivation-content h3 {
    font-size: var(--text-2xl);
  }

  .motivation-content p {
    font-size: var(--text-base);
  }
}

@media (max-width: 480px) {
  .pointers-bg::before {
    top: 100px;
  }

  .pointer-stats {
    flex-direction: column;
    gap: 12px;
  }
}
</style>